// "Vorschläge aus Mails" (the Gmail add-on): the user's own open
// mail proposals for this project, under the Focus selection screen's choices
// beside the "Meetings" button. Not a page of its own: the review in the web
// app is this list. One row per proposal: a mail of several topics is
// several rows, each acted on by its topic id.
//
// It renders only when BOTH hold:
//   - `GET /api/gmail/link` says the add-on is on here and the user's Google
//     account is linked (`enabled && linked`), and
//   - `GET /api/gmail/proposals?projectId=` has a row whose live status is
//     `new` (still a proposal). Accepted, declined, done and unread rows
//     (`status: null`) are left out.
// With the add-on off, either read failing, or nothing open, the screen is
// exactly what it was without it.
//
// Each row: the title, `#iid`, the "Neu" chip, "Übernehmen" / "Verwerfen" and
// a link to the issue view. Every action is decided on the server after a
// fresh read; the list reads again after each click, success or not, and one
// line says what happened (catalog copy for the closed code, never the body).
// The line keeps the section on screen when the last row leaves it.

import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { useRouter } from "@tanstack/react-router";

import { isUserErrorCode } from "@loom/shared";
import type { MailProposalRowDto } from "@loom/shared/gmail";
import { stripForgeNeutraliser } from "@loom/shared/issue-body";

import { readGmailLink } from "../../account/gmailApi";
import { useT, type MessageKey } from "../../i18n/index.js";
import { failureCodeOf, type HttpFailure } from "../../lib/http";

import { listMailProposals, mailProposalAction, type MailProposalAction } from "./mailProposalsApi";

/** The issue view of one proposal (`/projects/$projectId/issues/$iid`). */
export const mailProposalHref = (projectId: number, iid: number): string =>
  `/projects/${projectId}/issues/${iid}`;

/** Server codes Accept / Decline answer, one line each. */
const CODE_COPY: Readonly<Record<string, MessageKey>> = {
  not_pending: "focus.mail.error.changed",
  public_project: "focus.mail.error.publicProject",
  not_found: "focus.mail.error.gone",
  access_denied: "focus.mail.error.forbidden",
};

export const mailFailureKeyOf = (f: HttpFailure): MessageKey => {
  const code = f.code;
  if (code !== null && Object.prototype.hasOwnProperty.call(CODE_COPY, code)) {
    return CODE_COPY[code] as MessageKey;
  }
  if (isUserErrorCode(code)) return `error.${code}` as MessageKey;
  if (f.status === 404) return "focus.mail.error.gone";
  if (f.status === 403 && code !== "cross_origin_request") return "focus.mail.error.forbidden";
  return "focus.mail.error.generic";
};

type Note =
  | { readonly kind: "done"; readonly key: MessageKey; readonly iid: number }
  | { readonly kind: "failed"; readonly key: MessageKey };

const DONE_COPY: Readonly<Record<MailProposalAction, MessageKey>> = {
  accept: "focus.mail.accepted",
  decline: "focus.mail.declined",
};

export function MailProposals({ projectId }: { readonly projectId: number }) {
  const t = useT();
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined;
  const [rows, setRows] = useState<readonly MailProposalRowDto[]>([]);
  const [linked, setLinked] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<Note | null>(null);

  const reload = useCallback(
    async (isAlive: () => boolean = () => true): Promise<void> => {
      try {
        const list = await listMailProposals(projectId);
        if (isAlive()) setRows(list.proposals.filter((r) => r.status === "new"));
      } catch {
        if (isAlive()) setRows([]);
      }
    },
    [projectId],
  );

  useEffect(() => {
    let alive = true;
    const isAlive = (): boolean => alive;
    setRows([]);
    setNote(null);
    setLinked(false);
    readGmailLink()
      .then((link) => {
        if (!isAlive() || link.enabled !== true || link.linked !== true) return;
        setLinked(true);
        return reload(isAlive);
      })
      .catch(() => {
        // Off on a failed read: the screen stays as it was without the list.
      });
    return () => {
      alive = false;
    };
  }, [reload]);

  const act = (row: MailProposalRowDto, action: MailProposalAction): void => {
    if (busy !== null) return;
    setBusy(row.topicId);
    setNote(null);
    mailProposalAction(row.topicId, action)
      .then(() => setNote({ kind: "done", key: DONE_COPY[action], iid: row.issueIid }))
      .catch((err: unknown) => setNote({ kind: "failed", key: mailFailureKeyOf(failureCodeOf(err)) }))
      .then(() => reload())
      .finally(() => setBusy(null));
  };

  const open = (event: MouseEvent<HTMLAnchorElement>, href: string): void => {
    if (router === undefined) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    void router.navigate({ href });
  };

  if (!linked || (rows.length === 0 && note === null)) return null;

  return (
    <section className="meet-section" aria-labelledby={`mail-proposals-${projectId}`}>
      <h2 id={`mail-proposals-${projectId}`} className="meet-section-title">
        {t("focus.mail.title")}
      </h2>
      {rows.length > 0 && (
        <ul className="meet-list">
          {rows.map((row) => {
            const title = row.title === null ? null : stripForgeNeutraliser(row.title);
            const href = mailProposalHref(row.projectId, row.issueIid);
            return (
              <li key={row.topicId} className="meet-row" data-topic={row.topicId}>
                <div className="meet-row-head">
                  <span className="meet-row-title">{title ?? t("focus.mail.untitled")}</span>
                  <span className="meet-meta">#{row.issueIid}</span>
                  <span className="focus-chip meet-status is-new">{t("meetings.issueStatus.new")}</span>
                </div>
                <div className="meet-actions">
                  <button
                    type="button"
                    className="focus-btn"
                    disabled={busy !== null}
                    onClick={() => act(row, "accept")}
                  >
                    {t("meetings.action.accept")}
                  </button>
                  <button
                    type="button"
                    className="focus-btn is-ghost"
                    disabled={busy !== null}
                    onClick={() => act(row, "decline")}
                  >
                    {t("meetings.action.decline")}
                  </button>
                  <a className="focus-deeplink" href={href} onClick={(e) => open(e, href)}>
                    {t("focus.mail.open")}
                  </a>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {note !== null &&
        (note.kind === "failed" ? (
          <div className="focus-error" role="alert">
            {t(note.key)}
          </div>
        ) : (
          <div className="focus-help" role="status">
            {t(note.key, { iid: note.iid })}
          </div>
        ))}
    </section>
  );
}
